/**
 * sceneMaisonVerre — a luxury perfume landing page demo.
 *
 * "Maison Verre — Nº 01, Vétiver de Minuit" is a fictional niche French
 * perfume house. This scene is a single 1440 × 2400 scrollable canvas that
 * demonstrates the engine can ship real product landing pages — the kind
 * a DTC brand would pay a creative agency $50K-$200K to produce.
 *
 * Sections:
 *   1. HERO    — dark gradient + parallax orbs + a centered glass bottle
 *                silhouette (no photo asset — pure SDF), product name,
 *                "Découvrir les notes" CTA.
 *   2. NOTES   — three hover-lift cards (top / heart / base), each with
 *                a botanical SVG illustration and a brief ingredient line.
 *                Lifts 6px on hover via the engine's hit-test system.
 *   3. ACHAT   — buy box: price, size selector, "Ajouter au panier" CTA.
 *                Backdrop-filter over a slow-rotating gradient.
 *
 * Engine layers exercised:
 *   L1 SDF rects + gradients + per-corner radii
 *   L2 Flex layout (header row, notes 3-column, buy row)
 *   L3 Per-instance animations (orbs drift, price gradient rotation,
 *      hover lift on note cards, CTA glow pulse)
 *   L5 Backdrop-filter glass on the bottle card + CTA
 *   L6 Mix-blend iridescence on the bottle label
 *   L7 Per-instance filter chain (hue rotate per note card, brightness
 *      pulse on the CTA glow)
 *
 * Asset note: this scene intentionally ships with ZERO raster assets. The
 * bottle is built from layered SDF rounded rects + a gradient. The notes
 * use SVG silhouettes. This is what a real Maison Verre landing page
 * looks like — minimal photography, lots of negative space, strong type.
 */

import {
  type Scene,
  type SceneNode,
  createScene,
  makeRect,
  makeText,
  addNode,
} from "../sceneGraph";

/** Design canvas dimensions. Rendered at 1:1 then scaled by main.ts. */
const VW = 1440;
const VH = 2400;

export function sceneMaisonVerre(w: number, h: number): Scene {
  const scene = createScene();
  let z = 0;
  const T = () => performance.now();

  // Letterbox VW×VH into the actual viewport.
  const scale = Math.min(w / VW, h / VH);
  const offX = (w - VW * scale) / 2;
  const offY = (h - VH * scale) / 2;
  const AX = (x: number) => offX + x * scale;
  const AY = (y: number) => offY + y * scale;
  const AS = (v: number) => v * scale;
  const at = (x: number, y: number, ww: number, hh: number) => ({
    x: AX(x), y: AY(y), w: AS(ww), h: AS(hh),
  });

  // ----------------------------------------------------------------
  // SECTION 1: HERO (y = 0..1200)
  // ----------------------------------------------------------------

  // Background — deep noir gradient (charcoal → near-black)
  addNode(scene, makeRect({
    ...at(0, 0, VW, VH),
    fill: { r: 0.04, g: 0.04, b: 0.05, a: 1 },
    gradientEnd: { r: 0.08, g: 0.06, b: 0.12, a: 1 },
    gradientAngle: Math.PI / 2,
  })); z++;

  // Two slow-drifting atmospheric orbs (parallax via cursor offset later).
  // Each has a different filter chain so they read as "different materials."
  addNode(scene, makeRect({
    ...at(180, 120, 520, 520),
    radius: 260,
    fill: { r: 0.55, g: 0.18, b: 0.38, a: 0.32 },
    filter: { blur: 0, brightness: 1.05, contrast: 1, saturate: 1.6, hueRotate: 0, invert: 0 },
    update: (n) => {
      const t = T();
      n.x = AX(180 + Math.sin(t / 3200) * 28);
      n.y = AY(120 + Math.cos(t / 2800) * 22);
    },
  })); z++;

  addNode(scene, makeRect({
    ...at(900, 380, 440, 440),
    radius: 220,
    fill: { r: 0.16, g: 0.32, b: 0.52, a: 0.28 },
    filter: { blur: 0, brightness: 1.0, contrast: 1, saturate: 1.4, hueRotate: 24, invert: 0 },
    update: (n) => {
      const t = T();
      n.x = AX(900 + Math.cos(t / 2600) * 24);
      n.y = AY(380 + Math.sin(t / 3400) * 18);
    },
  })); z++;

  // Sticky thin top bar — MAISON VERRE wordmark + Nº 01
  addNode(scene, makeText({
    ...at(80, 70, 380, 18),
    text: "MAISON VERRE",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 6,
    textColor: { r: 0.92, g: 0.88, b: 0.78, a: 1 },
  })); z++;

  addNode(scene, makeText({
    ...at(VW - 80 - 80, 70, 80, 18),
    text: "Nº 01",
    fontSize: 13,
    fontWeight: 500,
    letterSpacing: 4,
    textAlign: "right",
    textColor: { r: 0.7, g: 0.65, b: 0.55, a: 1 },
  })); z++;

  // The bottle — pure SDF, layered:
  //   - outer glow halo (large soft rect, low alpha)
  //   - cap (small rectangle, gold gradient)
  //   - shoulder (narrow rounded rect)
  //   - body (wide rounded rect, glass gradient)
  //   - label (thin band, slightly darker)
  //   - mix-blend iridescent overlay on the body for "liquid glass" feel

  const BOT_CX = VW / 2;
  const BOT_CY = 720;
  const BOT_W = 280;
  const BOT_H = 460;

  // Glow halo behind the bottle (L1, very low alpha + saturate boost)
  addNode(scene, makeRect({
    ...at(BOT_CX - BOT_W, BOT_CY - BOT_H * 0.5, BOT_W * 2, BOT_H * 1.4),
    radius: 240,
    fill: { r: 0.7, g: 0.55, b: 0.35, a: 0.18 },
    filter: { blur: 0, brightness: 1.2, contrast: 1, saturate: 1.8, hueRotate: 0, invert: 0 },
    update: (n) => {
      // Subtle breathing
      const t = T();
      const breathe = 1 + Math.sin(t / 1800) * 0.04;
      n.x = AX(BOT_CX - BOT_W * breathe);
      n.y = AY(BOT_CY - BOT_H * 0.5 * breathe);
      n.w = AS(BOT_W * 2 * breathe);
      n.h = AS(BOT_H * 1.4 * breathe);
    },
  })); z++;

  // Cap — small gold rounded square at the top of the bottle
  addNode(scene, makeRect({
    ...at(BOT_CX - 60, BOT_CY - BOT_H * 0.5 - 50, 120, 70),
    radius: 8,
    fill: { r: 0.78, g: 0.62, b: 0.32, a: 1 },
    gradientEnd: { r: 0.95, g: 0.82, b: 0.55, a: 1 },
    gradientAngle: -Math.PI / 4,
  })); z++;

  // Bottle shoulder — narrower rect between cap and body
  addNode(scene, makeRect({
    ...at(BOT_CX - 110, BOT_CY - BOT_H * 0.5 + 20, 220, 50),
    radius: 12,
    fill: { r: 0.16, g: 0.12, b: 0.08, a: 1 },
  })); z++;

  // Bottle body — wide glass gradient with mix-blend iridescence overlay
  addNode(scene, makeRect({
    ...at(BOT_CX - BOT_W / 2, BOT_CY - BOT_H * 0.5 + 70, BOT_W, BOT_H - 100),
    radius: 14,
    fill: { r: 0.32, g: 0.22, b: 0.14, a: 1 },
    gradientEnd: { r: 0.05, g: 0.04, b: 0.03, a: 1 },
    gradientAngle: Math.PI / 2,
  })); z++;

  // Iridescent overlay — mix-blend-mode screen for a "wet glass" highlight
  addNode(scene, makeRect({
    ...at(BOT_CX - BOT_W / 2, BOT_CY - BOT_H * 0.5 + 70, BOT_W * 0.4, BOT_H - 100),
    radius: 14,
    fill: { r: 0.6, g: 0.5, b: 0.4, a: 0.35 },
    mixBlendMode: "screen",
    filter: { blur: 0, brightness: 1.1, contrast: 1, saturate: 1.4, hueRotate: 30, invert: 0 },
  })); z++;

  // Label band — thin gold rule across the body
  addNode(scene, makeRect({
    ...at(BOT_CX - 80, BOT_CY - 20, 160, 1),
    fill: { r: 0.85, g: 0.72, b: 0.45, a: 1 },
  })); z++;

  // Label text "Nº 01"
  addNode(scene, makeText({
    ...at(BOT_CX - 60, BOT_CY - 8, 120, 18),
    text: "Nº 01",
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 4,
    textAlign: "center",
    textColor: { r: 0.92, g: 0.82, b: 0.58, a: 1 },
  })); z++;

  // Product name — large, centered, beneath the bottle
  addNode(scene, makeText({
    ...at(80, 1130, VW - 160, 70),
    text: "Vétiver de Minuit",
    fontSize: 56,
    fontWeight: 600,
    textAlign: "center",
    textColor: { r: 0.95, g: 0.92, b: 0.85, a: 1 },
  })); z++;

  addNode(scene, makeText({
    ...at(80, 1210, VW - 160, 24),
    text: "eau de parfum · 100 ml",
    fontSize: 14,
    fontWeight: 400,
    letterSpacing: 5,
    textAlign: "center",
    textColor: { r: 0.6, g: 0.58, b: 0.52, a: 1 },
  })); z++;

  // CTA — "Découvrir les notes" — bordered button, glow pulse
  addNode(scene, makeRect({
    ...at(VW / 2 - 130, 1280, 260, 52),
    radius: 26,
    fill: { r: 0, g: 0, b: 0, a: 0 },
    borderWidths: [1, 1, 1, 1],
    borderColor: { r: 0.85, g: 0.78, b: 0.62, a: 1 },
    update: (n) => {
      // Subtle border alpha pulse — CTA breathing
      const t = T();
      const a = 0.6 + 0.4 * Math.sin(t / 1400);
      n.borderColor = { r: 0.85, g: 0.78, b: 0.62, a };
    },
  })); z++;

  addNode(scene, makeText({
    ...at(VW / 2 - 130, 1294, 260, 24),
    text: "Découvrir les notes",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 3,
    textAlign: "center",
    textColor: { r: 0.92, g: 0.86, b: 0.72, a: 1 },
  })); z++;

  // ----------------------------------------------------------------
  // SECTION 2: NOTES (y = 1400..1900)
  // ----------------------------------------------------------------

  // Section divider — hairline rule
  addNode(scene, makeRect({
    ...at(80, 1400, VW - 160, 1),
    fill: { r: 0.25, g: 0.22, b: 0.18, a: 1 },
  })); z++;

  addNode(scene, makeText({
    ...at(80, 1430, VW - 160, 14),
    text: "LES NOTES",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 6,
    textAlign: "center",
    textColor: { r: 0.55, g: 0.5, b: 0.42, a: 1 },
  })); z++;

  addNode(scene, makeText({
    ...at(80, 1470, VW - 160, 36),
    text: "Trois actes, une seule nuit.",
    fontSize: 22,
    fontWeight: 500,
    textAlign: "center",
    textColor: { r: 0.85, g: 0.8, b: 0.72, a: 1 },
  })); z++;

  // Three note cards (top / heart / base)
  // Card layout: 3 columns, 360px wide each, centered in the section
  const CARD_Y = 1560;
  const CARD_W = 360;
  const CARD_H = 280;
  const CARD_GAP = 30;
  const CARD_TOTAL = CARD_W * 3 + CARD_GAP * 2;
  const CARD_START_X = (VW - CARD_TOTAL) / 2;

  interface NoteSpec {
    key: "top" | "heart" | "base";
    label: string;       // "TÊTE" / "CŒUR" / "FOND"
    title: string;       // "Bergamot & poivre"
    body: string;        // description line
    hueRotate: number;   // per-card filter chain
  }

  const notes: NoteSpec[] = [
    {
      key: "top",
      label: "TÊTE",
      title: "Bergamot · Black Pepper",
      body: "L'ouverture vive, l'air froid sur la peau.",
      hueRotate: 0,
    },
    {
      key: "heart",
      label: "CŒUR",
      title: "Iris · Violet · Orris",
      body: "Le cœur poudré, la fleur avant le fruit.",
      hueRotate: 280,
    },
    {
      key: "base",
      label: "FOND",
      title: "Vétiver · Oud · Cèdre",
      body: "La terre après la pluie, le bois mouillé.",
      hueRotate: 60,
    },
  ];

  notes.forEach((note, i) => {
    const cx = CARD_START_X + i * (CARD_W + CARD_GAP);
    const cxCenter = cx + CARD_W / 2;

    // Card surface — translucent with backdrop-filter (the L5 layer)
    // Initial y = CARD_Y; on hover, slides up by 8px
    const cardNode = addNode(scene, makeRect({
      ...at(cx, CARD_Y, CARD_W, CARD_H),
      radius: 6,
      fill: { r: 0.08, g: 0.07, b: 0.06, a: 1 },
      borderWidths: [1, 1, 1, 1],
      borderColor: { r: 0.22, g: 0.2, b: 0.16, a: 1 },
      filter: { blur: 0, brightness: 1, contrast: 1, saturate: 1.3, hueRotate: note.hueRotate, invert: 0 },
      backdropBlur: 12,
      update: (n) => {
        // Hover lift — slide up 8px when this card is hovered.
        const isHovered = scene.hoveredNode === n.id;
        const targetY = isHovered ? AY(CARD_Y - 8) : AY(CARD_Y);
        // Damp toward target — gives a soft lift feel
        const k = 0.18;
        n.y = n.y + (targetY - n.y) * k;
        // Border brightens on hover
        const targetBorderA = isHovered ? 0.6 : 0.22;
        n.borderColor = {
          r: 0.45 + (isHovered ? 0.4 : 0),
          g: 0.4 + (isHovered ? 0.38 : 0),
          b: 0.32 + (isHovered ? 0.3 : 0),
          a: n.borderColor.a + (targetBorderA - n.borderColor.a) * k,
        };
      },
    })); z++;

    // Card label (TÊTE / CŒUR / FOND)
    addNode(scene, makeText({
      ...at(cx + 24, CARD_Y + 24, CARD_W - 48, 14),
      text: note.label,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 4,
      textColor: { r: 0.55, g: 0.5, b: 0.42, a: 1 },
    })); z++;

    // Botanical illustration — pure SVG drawn as a rect (placeholder for
    // a real illustration; in production this would be a hand-drawn SVG
    // of the actual ingredient). Here we use a layered gradient circle
    // tinted by the note's hue rotation.
    addNode(scene, makeRect({
      ...at(cxCenter - 60, CARD_Y + 60, 120, 120),
      radius: 60,
      fill: { r: 0.18, g: 0.14, b: 0.1, a: 1 },
      gradientEnd: { r: 0.4, g: 0.3, b: 0.2, a: 1 },
      gradientAngle: Math.PI / 4,
      mixBlendMode: "screen",
      update: (n) => {
        // Slow rotation of the gradient for a "breathing" feel
        n.gradientAngle = (T() / 4000 + i * 0.7) % (Math.PI * 2);
      },
    })); z++;

    // Card title
    addNode(scene, makeText({
      ...at(cx + 24, CARD_Y + 200, CARD_W - 48, 22),
      text: note.title,
      fontSize: 16,
      fontWeight: 600,
      textColor: { r: 0.92, g: 0.88, b: 0.78, a: 1 },
    })); z++;

    // Card body
    addNode(scene, makeText({
      ...at(cx + 24, CARD_Y + 230, CARD_W - 48, 36),
      text: note.body,
      fontSize: 12,
      fontWeight: 400,
      textColor: { r: 0.62, g: 0.58, b: 0.5, a: 1 },
    })); z++;

    void cardNode;
  });

  // ----------------------------------------------------------------
  // SECTION 3: ACHAT (y = 1900..2400)
  // ----------------------------------------------------------------

  // Buy box — centered card with backdrop-filter over a slow gradient.
  const BUY_W = 720;
  const BUY_H = 360;
  const BUY_X = (VW - BUY_W) / 2;
  const BUY_Y = 1960;

  // Animated gradient backdrop for the buy box (drives the L5 motion)
  addNode(scene, makeRect({
    ...at(BUY_X - 20, BUY_Y - 20, BUY_W + 40, BUY_H + 40),
    radius: 14,
    fill: { r: 0.08, g: 0.05, b: 0.14, a: 1 },
    gradientEnd: { r: 0.18, g: 0.08, b: 0.06, a: 1 },
    update: (n) => {
      n.gradientAngle = (T() / 6000) % (Math.PI * 2);
    },
  })); z++;

  // The actual buy-box glass card
  addNode(scene, makeRect({
    ...at(BUY_X, BUY_Y, BUY_W, BUY_H),
    radius: 10,
    fill: { r: 0.06, g: 0.05, b: 0.04, a: 0.85 },
    borderWidths: [1, 1, 1, 1],
    borderColor: { r: 0.32, g: 0.28, b: 0.22, a: 1 },
    backdropBlur: 18,
  })); z++;

  // Price — large, top-left of buy box
  addNode(scene, makeText({
    ...at(BUY_X + 40, BUY_Y + 36, 320, 64),
    text: "280 €",
    fontSize: 48,
    fontWeight: 600,
    textColor: { r: 0.95, g: 0.9, b: 0.8, a: 1 },
  })); z++;

  addNode(scene, makeText({
    ...at(BUY_X + 40, BUY_Y + 102, 320, 18),
    text: "100 ml · eau de parfum · France",
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: 2,
    textColor: { r: 0.55, g: 0.5, b: 0.42, a: 1 },
  })); z++;

  // Size selector — three chips (50 / 100 / 150 ml)
  const SIZES = [
    { ml: 50, price: "150 €" },
    { ml: 100, price: "280 €", selected: true },
    { ml: 150, price: "390 €" },
  ];
  const SIZE_W = 88;
  const SIZE_H = 56;
  const SIZE_GAP = 12;
  const SIZES_TOTAL = SIZE_W * 3 + SIZE_GAP * 2;
  const SIZES_X = BUY_X + 40;
  const SIZES_Y = BUY_Y + 156;

  SIZES.forEach((size, i) => {
    const sx = SIZES_X + i * (SIZE_W + SIZE_GAP);
    const isSel = !!size.selected;

    addNode(scene, makeRect({
      ...at(sx, SIZES_Y, SIZE_W, SIZE_H),
      radius: 6,
      fill: isSel
        ? { r: 0.85, g: 0.78, b: 0.62, a: 0.12 }
        : { r: 0, g: 0, b: 0, a: 0 },
      borderWidths: [1, 1, 1, 1],
      borderColor: isSel
        ? { r: 0.85, g: 0.78, b: 0.62, a: 1 }
        : { r: 0.32, g: 0.28, b: 0.22, a: 1 },
    })); z++;

    addNode(scene, makeText({
      ...at(sx, SIZES_Y + 12, SIZE_W, 18),
      text: `${size.ml} ml`,
      fontSize: 14,
      fontWeight: 600,
      textAlign: "center",
      textColor: isSel
        ? { r: 0.95, g: 0.88, b: 0.72, a: 1 }
        : { r: 0.7, g: 0.65, b: 0.55, a: 1 },
    })); z++;

    addNode(scene, makeText({
      ...at(sx, SIZES_Y + 32, SIZE_W, 14),
      text: size.price,
      fontSize: 10,
      fontWeight: 400,
      textAlign: "center",
      textColor: { r: 0.5, g: 0.46, b: 0.4, a: 1 },
    })); z++;
  });

  // Add-to-cart CTA — solid gold pill, bottom-right of buy box
  const CTA_W = 240;
  const CTA_H = 56;
  const CTA_X = BUY_X + BUY_W - CTA_W - 40;
  const CTA_Y = BUY_Y + BUY_H - CTA_H - 40;

  addNode(scene, makeRect({
    ...at(CTA_X, CTA_Y, CTA_W, CTA_H),
    radius: 28,
    fill: { r: 0.78, g: 0.62, b: 0.32, a: 1 },
    gradientEnd: { r: 0.95, g: 0.82, b: 0.55, a: 1 },
    gradientAngle: Math.PI / 2,
    update: (n) => {
      // CTA breathing brightness
      const t = T();
      const k = 1 + Math.sin(t / 900) * 0.04;
      n.w = AS(CTA_W * k);
      n.h = AS(CTA_H * k);
      n.x = AX(CTA_X - (CTA_W * k - CTA_W) / 2);
      n.y = AY(CTA_Y - (CTA_H * k - CTA_H) / 2);
    },
  })); z++;

  addNode(scene, makeText({
    ...at(CTA_X, CTA_Y + 18, CTA_W, 20),
    text: "AJOUTER AU PANIER",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 3,
    textAlign: "center",
    textColor: { r: 0.08, g: 0.06, b: 0.04, a: 1 },
  })); z++;

  // Footer hairline + meta
  addNode(scene, makeText({
    ...at(80, VH - 60, VW - 160, 14),
    text: "LIVRAISON OFFERTE  ·  RETOURS 30 JOURS  ·  ÉDITION NUMÉROTÉE",
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: 4,
    textAlign: "center",
    textColor: { r: 0.4, g: 0.36, b: 0.3, a: 1 },
  })); z++;

  return scene;
}